import React, { useRef, useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { useField } from 'formik';
import { Q1Value } from 'models/documents';
import { DataGridComplete, DataGridRow } from 'components/DataGrid';
import { BoundingBox, HTableValues } from 'models/response';
import utils from 'utils';
import DialogInput from 'components/Input/DialogInput';
import { useOptionsSearchHTable } from '../DocumentDetails/Q1/hooks/useOptionsSearchHTable';

export interface CellDropdown {
  name: string;
  focus: boolean;
  onFocus: (cell?: { pageNumber: number; boundingBox: BoundingBox; content: string }) => void;
  row: DataGridRow;
  code: number;
  key: number;
  dialogTextInput: boolean;
  link: string;
  idDocType: number;
  idCompany: number;
  hTableValues: HTableValues[];
  gridRef?: any;
  disabled?: boolean;
}

const CellDialogInput = ({
  name,
  focus,
  onFocus,
  row,
  code,
  gridRef,
  disabled,
  key,
  dialogTextInput,
  link,
  idDocType,
  idCompany,
  hTableValues,
}: CellDropdown) => {
  const [field, , helpers] = useField<Q1Value>(name);
  const selectRef = useRef<HTMLElement | null>(null);

  const fieldValue = field.value.body?.find(({ id }) => id === row.id);
  const fieldContent = fieldValue ? fieldValue[code]?.content : '';

  const t = utils.intl.useTranslator();

  const [currentValue, setCurrentValue] = useState<{ value: string; label: string } | null>(
    utils.input.buildCurrentValue({
      fieldContent,
      hTableValues,
      optionFormat: key,
    }),
  );

  const optionType = utils.input.getLabelTypeByKey(key);

  const searchByCode = !optionType.includes('description');
  const { loadOptions } = useOptionsSearchHTable({
    code: searchByCode,
    hTable: link,
    idDocType,
    idCompany,
    optionFormat: key,
  });
  const handleChange = (option: { value: string; label: string } | null) => {
    setCurrentValue(option);

    const body: DataGridComplete[] | undefined = field.value.body?.map((elem: DataGridComplete) => {
      if (elem.id === row.id) {
        return {
          ...elem,
          [code]: {
            content: option?.value || '',
            pageNumber: -1,
            boundingBox: {
              x1: -1,
              y1: -1,
              x2: -1,
              y2: -1,
            },
          },
        };
      }
      return elem;
    });

    if (body) helpers.setValue({ ...field.value, body });
  };

  useUpdateEffect(() => {
    if (focus) {
      selectRef.current?.focus();
    }
  }, [focus]);

  useEffect(() => {
    if (!focus) return;
    const isMac = navigator.platform.toUpperCase().includes('MAC');
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((isMac && event.metaKey && event.key === 'c') || (!isMac && event.ctrlKey && event.key === 'c')) {
        navigator.clipboard.writeText(
          typeof currentValue === 'object' && currentValue?.label ? currentValue.label : '',
        );
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focus, currentValue]);

  return (
    <DialogInput
      isClearable={focus}
      setRef={selectRef}
      value={currentValue}
      loadOptions={loadOptions}
      onChange={handleChange}
      onFocus={() => onFocus(fieldValue ? fieldValue[code] : null)}
      menuIsOpen={focus && !disabled}
      noBorder
      isPortal
      minWidthLabel="auto"
      closeMenuOnScroll={(event) => {
        if (event.target instanceof HTMLElement) {
          if (
            event.target?.className?.includes('MenuList') ||
            event.target?.className?.includes('ResizableTwoRowsLayout') ||
            document.querySelector('.react-select > div > div')?.className === event.target?.className ||
            event.target?.className?.includes('cell-text-input') ||
            event.target.parentElement?.className?.includes('menu') ||
            event.target.parentElement?.className?.includes('BodyContainer')
          ) {
            return false;
          }
          if (gridRef?.current) {
            gridRef.current.focusCell(null);
          }
          return true;
        }

        return true;
      }}
      disabled={disabled}
      dialogTextInput={dialogTextInput}
      loadingOptionsMessage={t('loading_options')}
      noOptionsMessage={t('no_options')}
    />
  );
};

export default CellDialogInput;
